import { useQuery } from '@tanstack/react-query'
import { dashboardConfigApi } from '@/lib/api'
import { BaseWidget } from './BaseWidget'
import { Folder, CheckSquare, Calendar, AlertCircle } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import type { ProjectStatsData, RecentProjectData, TaskData } from '@/types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

const PRIORITY_COLORS: Record<string, string> = {
  HIGH: 'text-red-500',
  MEDIUM: 'text-amber-500',
  LOW: 'text-green-500',
}

// Project Stats Widget
export function ProjectStatsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'PROJECT_STATS'],
    queryFn: () => dashboardConfigApi.getWidgetData<ProjectStatsData>('PROJECT_STATS'),
  })

  const stats = data?.data

  return (
    <BaseWidget
      title={t('dashboard.projectStats')}
      icon={<Folder className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold">{stats?.active_projects || 0}</p>
          <p className="text-xs text-muted-foreground">{t('projects.active')}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{stats?.total_tasks || 0}</p>
          <p className="text-xs text-muted-foreground">{t('projects.tasks')}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-red-500">{stats?.overdue_tasks || 0}</p>
          <p className="text-xs text-muted-foreground">{t('projects.overdue')}</p>
        </div>
      </div>
    </BaseWidget>
  )
}

// Recent Projects Widget
export function RecentProjectsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'RECENT_PROJECTS'],
    queryFn: () => dashboardConfigApi.getWidgetData<RecentProjectData[]>('RECENT_PROJECTS'),
  })

  const projects = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.recentProjects')}
      icon={<Folder className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noRecentProjects')}</p>
        ) : (
          projects.map((project) => (
            <Link
              key={project.project_id}
              to={`/projects/${project.project_id}`}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div
                className="w-8 h-8 rounded flex items-center justify-center text-white text-sm"
                style={{ backgroundColor: project.project_color }}
              >
                {project.project_icon === 'folder' ? '📁' : project.project_icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{project.project_name}</p>
                <p className="text-xs text-muted-foreground">{project.board_count} {t('projects.boards')}</p>
              </div>
            </Link>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

// My Tasks Widget
export function MyTasksWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'MY_TASKS'],
    queryFn: () => dashboardConfigApi.getWidgetData<TaskData[]>('MY_TASKS'),
  })

  const tasks = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.myTasks')}
      icon={<CheckSquare className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-2">
        {tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noTasks')}</p>
        ) : (
          tasks.map((task) => (
            <div key={task.card_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50">
              <CheckSquare className={`h-4 w-4 ${task.is_completed ? 'text-green-500' : 'text-muted-foreground'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{task.card_title}</p>
                <p className="text-xs text-muted-foreground truncate">{task.project_name} / {task.board_name}</p>
              </div>
              {task.priority && (
                <span className={`text-xs ${PRIORITY_COLORS[task.priority] || ''}`}>{task.priority}</span>
              )}
            </div>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

// Upcoming Deadlines Widget
export function UpcomingDeadlinesWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'UPCOMING_DEADLINES'],
    queryFn: () => dashboardConfigApi.getWidgetData<TaskData[]>('UPCOMING_DEADLINES'),
  })

  const tasks = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.upcomingDeadlines')}
      icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-2">
        {tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noUpcomingDeadlines')}</p>
        ) : (
          tasks.map((task) => (
            <div key={task.card_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{task.card_title}</p>
                <p className="text-xs text-muted-foreground">{task.project_name}</p>
              </div>
              <p className="text-xs text-amber-500">{formatDate(task.due_date || '')}</p>
            </div>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

